import * as Sentry from "@sentry/react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router";
import React from "react";
import { getSessionTraceId } from "@holisto-web/session-trace-id";
import { checkIsAgentSubdomain, getSentryLogSampleRate, getSentryTraceSampleRate } from "@holisto-web/sentry";
import {
  createSentryRequestMatcher,
  processSentryEvent,
  setSentryTag,
  syncSentryCookieContext,
  syncSentryRouteContext,
  installDomMutationDiagnostics,
} from "@modules/sentry";

installDomMutationDiagnostics();

if (window.SENTRY_DSN_CLIENT) {
  const hostname = window.location.hostname;
  const isAgentSubdomain = checkIsAgentSubdomain(hostname);
  const sentryLogSampleRate = getSentryLogSampleRate({ isAgentSubdomain });
  const sentryRequestMatcher = createSentryRequestMatcher({
    currentOrigin: window.location.origin,
    allowedOrigins: [window.CLIENT_API_BASE_URL, window.location.origin],
    allowedServerDomain: window.SERVER_DOMAIN,
  });

  const commonTags = {
    application_version: process.env.REACT_APP_VERSION,
  };
  Sentry.init({
    dsn: window.SENTRY_DSN_CLIENT,
    release: `${process.env.REACT_APP_BRAND_NAME}@${process.env.REACT_APP_VERSION}`,
    sendDefaultPii: true,
    environment: window.APP_ENVIRONMENT,
    initialScope: {
      tags: { ...commonTags },
    },
    maxBreadcrumbs: 30,
    ignoreTransactions: [
      "GET /health",
      "GET /version",
      "GET /status",
      /^\/version\.json/,
      /\.(css|js|png|jpg|jpeg|gif|ico|svg|woff|woff2|ttf|map|json|webp|xml)$/i,
    ],
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        shouldCreateSpanForRequest: (url) => {
          if (isAgentSubdomain) {
            return false;
          }

          if (!sentryRequestMatcher.isAllowedRequest(url)) {
            return false;
          }

          // Do not trace requests to the version endpoint
          if (url.includes("/version.json")) {
            return false;
          }

          // Do not trace static assets (adjust extensions as needed)
          // Note: This only applies to assets fetched via XHR/Fetch (JS, JSON, etc.)
          // Standard <img src> tags are not traced by default anyway.
          if (url.match(/\.(css|js|png|jpg|jpeg|gif|ico|svg|woff|woff2|ttf|map|json|webp|xml)$/i)) {
            return false;
          }

          return true;
        },
      }),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    tracePropagationTargets: sentryRequestMatcher.tracePropagationTargets,
    enableLogs: sentryLogSampleRate > 0,
    ...(sentryLogSampleRate < 1
      ? {
          beforeSendLog: (log) => (Math.random() < sentryLogSampleRate ? log : null),
        }
      : {}),
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: getSentryTraceSampleRate({ hostname }),
  });
  setSentryTag("application_version", commonTags.application_version);
  setSentryTag("sessionTraceId", getSessionTraceId(window.SESSION_TRACE_ID));
  syncSentryCookieContext();
  syncSentryRouteContext({
    pathname: window.location.pathname,
    search: window.location.search,
    url: window.location.href,
  });

  Sentry.addEventProcessor((event) => {
    return processSentryEvent(event, {
      sessionTraceId: getSessionTraceId(window.SESSION_TRACE_ID),
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
    });
  });
}
